import classNames from 'classnames';
import { useCallback, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';

import { getDefaultPreviewAt, isoToPickerDate, pickerDateToIso, PreviewMode } from '../utils/displayWindow';

const FEED_PREVIEW_URL = `${process.env.REACT_APP_PINTO_BOOK_URL}/dev/feed-preview?hideNavbarAndFooter=true`;

const IFRAME_FEED_PREVIEW_NAME = 'feedPreview';
interface IPropsFeedPreviewPresenter {
  previewMode: PreviewMode;
  onChangePreviewMode: (mode: PreviewMode) => void;
  onSavePreview: (mode: PreviewMode) => Promise<boolean>;
  disabled?: boolean;
}

const MODE_OPTIONS: { type: PreviewMode['type']; label: string }[] = [
  { type: 'now', label: 'Now' },
  { type: 'at', label: 'At time…' },
  { type: 'all', label: 'All sections' },
];

function FeedPreviewPresenter(props: IPropsFeedPreviewPresenter) {
  const { previewMode, onChangePreviewMode, onSavePreview, disabled = false } = props;

  const [isFirstRefresh, setIsFirstRefresh] = useState(true);

  const iFrameRef = useRef<HTMLIFrameElement>(null);

  // mode is passed explicitly so a mode change refreshes with the new value, not stale state
  const refresh = useCallback(
    async (mode: PreviewMode) => {
      if (!(await onSavePreview(mode))) return;

      if (iFrameRef.current) {
        iFrameRef.current.src = FEED_PREVIEW_URL;
      }
      setIsFirstRefresh(false);
    },
    [onSavePreview],
  );

  const changeMode = (mode: PreviewMode) => {
    onChangePreviewMode(mode);
    refresh(mode);
  };

  const onSelectType = (type: PreviewMode['type']) => {
    if (type === previewMode.type) return;
    changeMode(type === 'at' ? { type, previewAt: getDefaultPreviewAt() } : ({ type } as PreviewMode));
  };

  const onChangePreviewAt = (date: Date | null) => {
    if (date) changeMode({ type: 'at', previewAt: pickerDateToIso(new Date(date.setSeconds(0, 0))) as string });
  };

  return (
    <>
      <div className="flex items-center justify-between px-12 mb-4">
        <p>Preview</p>
        <button onClick={() => refresh(previewMode)} className="btn btn-outline" disabled={disabled}>
          Refresh
        </button>
      </div>

      <div className="px-12 mb-4 space-y-2">
        <div role="radiogroup" aria-label="Preview time mode" className="space-y-1">
          {MODE_OPTIONS.map(({ type, label }) => (
            <div key={type} className="flex items-center h-8 space-x-3">
              <label className="flex items-center w-[140px] space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="preview-mode"
                  className="radio radio-sm radio-error"
                  checked={previewMode.type === type}
                  disabled={disabled}
                  onChange={() => onSelectType(type)}
                />
                <span className={classNames({ 'font-semibold': previewMode.type === type })}>{label}</span>
              </label>
              {type === 'at' && (
                <>
                  <span id="preview-time-label" className="sr-only">
                    Preview time (Bangkok time)
                  </span>
                  <DatePicker
                    dateFormat="dd/MM/yyyy HH:mm"
                    timeFormat="HH:mm"
                    showTimeSelect
                    timeIntervals={5}
                    popperProps={{ strategy: 'fixed' }}
                    ariaLabelledBy="preview-time-label"
                    wrapperClassName="w-[180px]"
                    className="w-full input input-bordered input-sm"
                    disabled={disabled || previewMode.type !== 'at'}
                    selected={isoToPickerDate(previewMode.type === 'at' ? (previewMode.previewAt as string) : getDefaultPreviewAt())}
                    onChange={onChangePreviewAt}
                  />
                </>
              )}
            </div>
          ))}
        </div>
        {disabled && (
          <p role="status" className="text-xs font-medium text-error">
            Preview is disabled: fix the Sections marked Invalid first.
          </p>
        )}
        <p className="text-xs opacity-70">Preview time only decides which Template Sections show; items inside each Section are current.</p>
      </div>

      <div className="relative h-[95%] border mockup-window border-base-300 mr-16">
        {isFirstRefresh && <div className="absolute top-0 w-full h-full bg-opacityWhite80"></div>}
        <div className="flex flex-col items-center justify-center h-full px-4 py-16 bg-white">
          <iframe ref={iFrameRef} id={IFRAME_FEED_PREVIEW_NAME} name={IFRAME_FEED_PREVIEW_NAME} title="Feed preview" className="w-full h-full"></iframe>
        </div>
      </div>
    </>
  );
}

export default FeedPreviewPresenter;
